import { useCallback, useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CarriageRecoveryView,
  Carrier,
} from "@shared-core/data/carriage-cost-types";
import {
  CARRIER_LABELS,
  formatMonthKey,
  formatPct,
  recoveryTone,
} from "@shared-core/domain/carriage-costs";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { StatCard } from "../../patterns/StatCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";

// Insights → Carriage recovery: what courier delivery cost against what was
// charged for it, per customer, for a month. Reads the composed
// /views/insights/carriage-recovery endpoint over the dbt carriage_recovery
// metric. Sorted worst recovery first — the point of the tab is the leak.

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; view: CarriageRecoveryView };

const TONE_CLASS: Record<ReturnType<typeof recoveryTone>, string> = {
  good: "text-emerald-700 dark:text-emerald-400",
  warn: "text-amber-700 dark:text-amber-400",
  bad: "text-red-700 dark:text-red-400",
  none: "text-muted-foreground",
};

const MONTHS = ["2026-09", "2026-08", "2026-07", "2026-06"];

export function CarriageRecoveryTab() {
  const adapter = useDataAdapter();
  const [month, setMonth] = useState("2026-08");
  const [carrier, setCarrier] = useState<Carrier | "all">("all");
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const view = await adapter.getInsightsCarriageRecovery({
        month,
        carrier,
      });
      setState({ status: "ready", view });
    } catch {
      setState({ status: "error" });
    }
  }, [adapter, month, carrier]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-40" aria-label="Month">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((m) => (
              <SelectItem key={m} value={m}>
                {formatMonthKey(m)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={carrier}
          onValueChange={(v) => setCarrier(v as Carrier | "all")}
        >
          <SelectTrigger className="w-36" aria-label="Carrier">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All carriers</SelectItem>
            <SelectItem value="dx">{CARRIER_LABELS.dx}</SelectItem>
            <SelectItem value="dpd">{CARRIER_LABELS.dpd}</SelectItem>
          </SelectContent>
        </Select>
        <Link
          to={paths.carriageCosts()}
          className="text-primary ml-auto text-sm underline hover:no-underline"
        >
          Upload invoices &amp; resolve exceptions
        </Link>
      </div>

      {state.status === "loading" && (
        <div className="border-border bg-surface text-muted-foreground flex min-h-[320px] items-center justify-center rounded-lg border text-sm">
          Loading carriage recovery…
        </div>
      )}
      {state.status === "error" && (
        <div className="border-border bg-surface text-muted-foreground flex min-h-[320px] items-center justify-center rounded-lg border text-sm">
          Carriage recovery could not be loaded.
        </div>
      )}
      {state.status === "ready" && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <StatCard
              label="Courier cost"
              value={formatCurrencyGbp(state.view.total_cost)}
            />
            <StatCard
              label="Carriage charged"
              value={formatCurrencyGbp(state.view.total_charged)}
            />
            <StatCard
              label="Recovery"
              value={
                <span
                  className={TONE_CLASS[recoveryTone(state.view.recovery_pct)]}
                >
                  {formatPct(state.view.recovery_pct)}
                </span>
              }
            />
            <StatCard
              label="Not on a job"
              value={formatCurrencyGbp(state.view.total_unallocated_cost)}
            />
            <StatCard
              label="Consignments"
              value={state.view.consignments.toLocaleString("en-GB")}
            />
          </div>

          <div className="border-border bg-surface overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Jobs priced</TableHead>
                  <TableHead className="text-right">Consignments</TableHead>
                  <TableHead className="text-right">Courier cost</TableHead>
                  <TableHead className="text-right">Not on a job</TableHead>
                  <TableHead className="text-right">Charged</TableHead>
                  <TableHead className="text-right">Recovery</TableHead>
                  <TableHead>Worst job</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.view.rows.map((row) => (
                  <TableRow key={row.customer_id}>
                    <TableCell className="font-medium">
                      {row.customer_name}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.jobs_with_known_charge === row.jobs
                        ? row.jobs
                        : `${row.jobs_with_known_charge} / ${row.jobs}`}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.consignments}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrencyGbp(row.carriage_cost)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-right">
                      {row.unallocated_cost > 0
                        ? formatCurrencyGbp(row.unallocated_cost)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrencyGbp(row.carriage_charged)}
                    </TableCell>
                    <TableCell
                      className={`text-right font-medium ${TONE_CLASS[recoveryTone(row.recovery_pct)]}`}
                    >
                      {formatPct(row.recovery_pct)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {row.worst_job_no ? (
                        <>
                          <span className="text-foreground">
                            {row.worst_job_no}
                          </span>
                          {row.worst_job_cost_pct_of_value !== null && (
                            <>
                              {" "}
                              · courier{" "}
                              {formatPct(row.worst_job_cost_pct_of_value)} of
                              job value
                            </>
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-muted-foreground text-xs">
            Recovery is carriage charged over the courier cost of the jobs whose
            charge we can read — the &ldquo;jobs priced&rdquo; count. Where
            carriage is priced inside the job, the job&rsquo;s margin is the
            measure instead; see the worst-job column.{" "}
            <strong>Not on a job</strong> is call-off cost that reached no job,
            so it carries no charge to compare: for customers invoiced by a
            month-end schedule that is most of their spend.
          </p>
        </>
      )}
    </div>
  );
}
